import { normalizeMediaUrl, objectUrl, parseMediaArray } from "@/lib/media";



export type AdminMediaPost = {

  is_drift?: boolean;

  type?: string;

  thumbnail_url?: string | null;

  media_urls?: string[] | null;

  video_metadata?: Record<string, unknown> | null;

  media?: unknown;

};



const VIDEO_FILE = /\.(mp4|webm|mov|m4v)(\?|$)/i;



function urlsFromPost(post: AdminMediaPost): string[] {

  const fromArray = (post.media_urls ?? [])

    .map((u) => normalizeMediaUrl(u))

    .filter(Boolean);



  if (fromArray.length) return fromArray;



  const variants = parseMediaArray(post.media);

  if (variants.length) {

    return variants

      .map((v) => v.full || v.medium || v.thumb || "")

      .filter(Boolean);

  }



  const meta = post.video_metadata;

  const variantsMeta = meta?.variants as Record<string, unknown> | undefined;

  const basePath = String(variantsMeta?.base_path ?? "").replace(/\/$/, "");

  const mainFile = String(variantsMeta?.main ?? variantsMeta?.medium ?? "video.mp4");

  if (basePath) {

    const path = mainFile.includes("/") ? mainFile : `${basePath}/${mainFile}`;

    return [path.includes("://") ? normalizeMediaUrl(path) : objectUrl(path)];

  }



  const thumb = post.thumbnail_url?.trim();

  return thumb ? [normalizeMediaUrl(thumb)] : [];

}



function isDriftMediaPath(url: string): boolean {

  const lower = url.toLowerCase();

  return (

    lower.includes("drifts.daywia.com") ||

    lower.includes("/drifts/") ||

    lower.startsWith("drifts/") ||

    lower.includes("/posts/video/") ||

    lower.startsWith("posts/video/")

  );

}



/** True only for actual video files — not drift thumbnails. */

export function isAdminVideoUrl(url: string): boolean {

  const lower = url.toLowerCase();

  if (VIDEO_FILE.test(lower)) return true;

  if (lower.includes("/video.mp4") || lower.includes("/video.webm")) return true;

  return false;

}



function videoFromDriftPath(url: string): string | null {

  const normalized = normalizeMediaUrl(url);

  if (isAdminVideoUrl(normalized)) return normalized;

  if (!isDriftMediaPath(normalized)) return null;



  if (/thumb/i.test(normalized)) {

    return normalized.replace(/thumb[^/?]*\.webp/i, "video.mp4");

  }



  const base = normalized.replace(/\/[^/?]+$/, "");

  return `${base}/video.mp4`;

}



export function resolveAdminVideoUrl(post: AdminMediaPost): string | null {

  const urls = urlsFromPost(post);

  const explicit = urls.find(isAdminVideoUrl);

  if (explicit) return explicit;



  for (const url of urls) {

    const derived = videoFromDriftPath(url);

    if (derived) return derived;

  }



  const thumb = post.thumbnail_url?.trim();

  if (thumb) {

    const derived = videoFromDriftPath(thumb);

    if (derived) return derived;

  }



  const meta = post.video_metadata;

  const variantsMeta = meta?.variants as Record<string, unknown> | undefined;

  const basePath = String(variantsMeta?.base_path ?? "").replace(/\/$/, "");

  if (basePath && isDriftMediaPath(basePath)) {

    return objectUrl(`${basePath}/video.mp4`);

  }



  if (post.is_drift || post.type === "video" || post.type === "drift") {

    const driftUrl = urls.find(isDriftMediaPath);

    if (driftUrl) return videoFromDriftPath(driftUrl);

  }



  return null;

}



export function resolveAdminImageUrl(post: AdminMediaPost): string | null {

  const urls = urlsFromPost(post);

  const still = urls.find((u) => !isAdminVideoUrl(u));

  if (still) return still;



  const thumb = post.thumbnail_url?.trim();

  if (thumb) return normalizeMediaUrl(thumb);



  return null;

}



export function resolveAdminPosterUrl(post: AdminMediaPost): string | null {

  const thumb = post.thumbnail_url?.trim();

  if (thumb) return normalizeMediaUrl(thumb);



  const urls = urlsFromPost(post);

  return urls.find((u) => !isAdminVideoUrl(u)) ?? null;

}

